import { Injectable } from '@angular/core';
import firebase from 'firebase/app';
import { AutoForwardJob, AutoForwardRule, AutoForwardSpoke } from '../models/auto-forwarding.model';

@Injectable({ providedIn: 'root' })
export class AutoForwardingService {
  private db = firebase.firestore();
  private manageRuleFn = firebase.functions().httpsCallable('autoForward-manageRule');

  async getRule(hubFacilityId: string, caseName: string): Promise<AutoForwardRule | null> {
    const ruleId = `${hubFacilityId}_${caseName}`;
    const snap = await this.db.collection('autoForwardRules').doc(ruleId).get();
    return snap.exists ? ({ id: snap.id, ...snap.data() } as AutoForwardRule) : null;
  }

  async getRecentJobs(hubFacilityId: string, caseName: string, days = 30): Promise<AutoForwardJob[]> {
    const since = new Date();
    since.setDate(since.getDate() - days);
    const snap = await this.db
      .collection('autoForwardJobs')
      .where('hubFacilityId', '==', hubFacilityId)
      .where('patientId', '==', caseName)
      .where('createdAt', '>=', firebase.firestore.Timestamp.fromDate(since))
      .orderBy('createdAt', 'desc')
      .limit(50)
      .get();
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as AutoForwardJob));
  }

  async getSpokesWithAeTitle(hubFacilityId: string): Promise<AutoForwardSpoke[]> {
    const snap = await this.db.collection('facilities').get();
    return snap.docs
      .filter(d => d.id !== hubFacilityId && d.data()['ae_title'])
      .map(d => ({ id: d.id, name: d.data()['name'] || d.id, ae_title: d.data()['ae_title'] }));
  }

  async getFacilityName(facilityId: string): Promise<string> {
    try {
      const snap = await this.db.collection('facilities').doc(facilityId).get();
      return snap.exists ? (snap.data()?.['name'] || facilityId) : facilityId;
    } catch {
      return facilityId;
    }
  }

  async upsertRule(
    hubFacilityId: string,
    caseName: string,
    destinationSpokeId: string,
    active = true
  ): Promise<{ ruleId: string }> {
    const result = await this.manageRuleFn({ operation: 'upsert', hubFacilityId, caseName, destinationSpokeId, active });
    return result.data;
  }

  async disableRule(hubFacilityId: string, caseName: string): Promise<void> {
    await this.manageRuleFn({ operation: 'disable', hubFacilityId, caseName });
  }

  async retryJob(hubFacilityId: string, jobId: string): Promise<void> {
    await this.manageRuleFn({ operation: 'retry', hubFacilityId, jobId });
  }
}
